/**
 * Expiring Documents List Component
 * TASK-361: Document Management & Compliance System
 *
 * Displays a list of documents that are expiring soon or have expired,
 * with actions to renew or set new expiration dates.
 */

import React, { useState } from 'react';
import {
  FileText,
  User,
  AlertTriangle,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  ExpiringDocument,
  getDocTypeLabel,
  formatDaysUntilExpiry,
} from '../../types/documentCompliance';
import { secureUploadLinkService } from '../../services/secureUploadLinkService';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import RenewalRequestModal from './RenewalRequestModal';

interface ExpiringDocumentsListProps {
  documents: ExpiringDocument[];
  emptyMessage?: string;
  onRefresh?: () => void;
  showAsExpired?: boolean;
}

const ExpiringDocumentsList: React.FC<ExpiringDocumentsListProps> = ({
  documents,
  emptyMessage = 'No documents to display',
  onRefresh,
  showAsExpired = false,
}) => {
  const navigate = useNavigate();
  const [renewingDocId, setRenewingDocId] = useState<string | null>(null);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<ExpiringDocument | null>(null);

  const handleRenewClick = (doc: ExpiringDocument) => {
    setSelectedDoc(doc);
    setShowRenewModal(true);
  };

  const navigateToClient = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`flex-shrink-0 ${showAsExpired ? 'text-red-500' : 'text-amber-500'}`}>
                {showAsExpired ? <XCircle size={20} /> : <AlertTriangle size={20} />}
              </div>
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {doc.filename}
                </p>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <User size={12} />
                    <button
                      onClick={() => navigateToClient(doc.client_id)}
                      className="hover:text-[#002B45] hover:underline"
                    >
                      {doc.client_name || 'Unknown Client'}
                    </button>
                  </span>
                  <span className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">
                    {getDocTypeLabel(doc.doc_type)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Expiry Info */}
              <div className="text-right">
                <p className={`text-sm font-medium ${
                  doc.days_until_expiry < 0 ? 'text-red-600' :
                  doc.days_until_expiry <= 7 ? 'text-red-600' :
                  doc.days_until_expiry <= 14 ? 'text-amber-600' : 'text-amber-500'
                }`}>
                  {formatDaysUntilExpiry(doc.days_until_expiry)}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(doc.expires_at).toLocaleDateString()}
                </p>
              </div>

              {/* Actions */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRenewClick(doc)}
                className="flex items-center gap-1.5"
              >
                <RefreshCw size={14} />
                Renew
              </Button>
            </div>
          </div>
        ))}
      </div>

      {showRenewModal && selectedDoc && (
        <RenewalRequestModal
          document={selectedDoc}
          isLoading={!!renewingDocId}
          onSubmit={async (newExpiryDate, notes) => {
            try {
              setRenewingDocId(selectedDoc.id);
              await secureUploadLinkService.markDocumentRenewed(
                selectedDoc.id,
                newExpiryDate,
                notes
              );
              toast.success('Document marked as renewed');
              setShowRenewModal(false);
              onRefresh?.();
            } catch (err) {
              console.error('Failed to mark document renewed:', err);
              toast.error('Failed to update document');
            } finally {
              setRenewingDocId(null);
            }
          }}
          onClose={() => setShowRenewModal(false)}
        />
      )}
    </>
  );
};

export default ExpiringDocumentsList;
